import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateOrganization } from '../../../../slices/organizationSlice';
import { updateCollection } from '../../../../slices/itemsSlice';
import { displayAlert } from '../../../../slices/feedbackSlice';

/**
 * Les deux noms publics de la bibliothèque.
 *
 * 🔴 Aucun écran ne permettait de les changer. Ils valaient donc encore le nom
 * généré à l'inscription — « <courriel> - default organization » — pour vingt-
 * deux bibliothèques sur trente et une, et ce nom est le TITRE de la vitrine
 * publique. L'adresse courriel de leur direction était publiée sur une page
 * ouverte à tous.
 *
 * ⚠️ Les deux noms sont distincts et le restent : une organisation peut tenir
 * plusieurs collections. Mais les deux sont publics, et la carte le dit avant
 * les champs plutôt qu'après.
 */
function Champ({
  libelle, aide, valeur, onEnregistrer,
}) {
  const { t } = useTranslation('myAccount');
  const [saisie, setSaisie] = useState(valeur);
  const [erreur, setErreur] = useState(null);

  return (
    <TextField
      variant="standard"
      fullWidth
      label={libelle}
      value={saisie}
      helperText={erreur || aide}
      error={Boolean(erreur)}
      sx={{ mb: 3 }}
      onChange={(evenement) => { setSaisie(evenement.target.value); setErreur(null); }}
      onBlur={() => {
        const propre = saisie.trim();
        // ⛔ Un nom vide n'est pas enregistré : ces deux champs sont publics,
        // et une vitrine sans titre vaut mieux qu'une vitrine qu'on a vidée
        // par accident en effaçant pour retaper.
        if (!propre) {
          setErreur(t('library.required'));
          setSaisie(valeur);
          return;
        }
        if (propre !== valeur) {
          onEnregistrer(propre);
        }
      }}
    />
  );
}

Champ.propTypes = {
  libelle: PropTypes.string.isRequired,
  aide: PropTypes.string.isRequired,
  valeur: PropTypes.string.isRequired,
  onEnregistrer: PropTypes.func.isRequired,
};

// ⚠️ Le nom d'attente posé par le serveur à la création d'une organisation
// (`NOM_PAR_DEFAUT` dans `src/accounts/models.py`). Le comparer est le seul
// moyen de savoir qu'une bibliothèque n'a jamais choisi son nom — l'API ne
// rend pas cette information autrement.
//
// ⛔ Si le serveur change ce texte, cette note cesse silencieusement de
// s'afficher. Elle ne rend rien faux pour autant : elle disparaît, c'est tout.
const NOM_DATTENTE = 'Ma bibliothèque';

export default function Bibliotheque({ organization, collection }) {
  const { t } = useTranslation('myAccount');
  const dispatch = useDispatch();

  const nomJamaisChoisi = (organization.name || '').trim() === NOM_DATTENTE;
  // ⛔ Le cadre ne parle PAS de l'adresse de contact. Elle est publique par
  // destination — c'est à elle qu'on écrit pour emprunter un livre — et la
  // plupart des bibliothèques garderont une boîte partagée. Signaler l'adresse
  // reprise du compte reviendrait à présenter comme un défaut à corriger ce
  // qui est, dans presque tous les cas, le bon réglage.

  const enregistrer = (action) => {
    dispatch(action)
      .unwrap()
      .then(() => dispatch(displayAlert(t('library.saved'), 'success')))
      // Le thunk pose déjà une alerte avec le message de l'API : une seconde
      // afficherait deux bandeaux pour un seul échec.
      .catch(() => {});
  };

  return (
    <Card>
      <CardHeader title={t('library.title')} titleTypographyProps={{ variant: 'h3' }} />
      <CardContent>
        <Typography component="p" variant="body1" sx={{ mb: 3 }}>
          {t('library.description')}
        </Typography>

        {/* 🔑 La note n'apparaît QUE s'il reste quelque chose à corriger, et
            elle nomme précisément quoi. Une note permanente devient un décor
            qu'on cesse de voir ; une note vague fait chercher.

            ⛔ Elle ne reproche rien et n'alarme pas : personne n'a rien mal
            fait, ces champs n'étaient simplement modifiables nulle part
            jusqu'ici. */}
        {nomJamaisChoisi && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderLeft: 4,
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          }}
        >
          <Typography component="p" variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
            {t('library.nudge_title')}
          </Typography>
          <Typography component="p" variant="body1">
            {t('library.nudge_name')}
          </Typography>
        </Box>
        )}

        <Champ
          libelle={t('library.org')}
          aide={t('library.org_help')}
          valeur={organization.name || ''}
          onEnregistrer={(nom) => enregistrer(
            updateOrganization({ id: organization.id, name: nom }),
          )}
        />

        {/* 🔑 L'adresse de contact est PUBLIQUE et distincte de celle du
            compte. C'est elle que la vitrine propose à qui veut emprunter — et
            jusqu'au 01/09/2026 c'était l'adresse de connexion de la
            propriétaire, publiée sans que personne l'ait choisi. */}
        <Champ
          libelle={t('library.contact')}
          aide={t('library.contact_help')}
          valeur={organization.contact_email || ''}
          onEnregistrer={(adresse) => enregistrer(
            updateOrganization({ id: organization.id, contact_email: adresse }),
          )}
        />

        {/* La collection n'existe pas toujours au premier rendu : le champ
            n'apparaît qu'une fois qu'on sait laquelle renommer. */}
        {collection && collection.id && (
          <Champ
            libelle={t('library.collection')}
            aide={t('library.collection_help')}
            valeur={collection.name || ''}
            onEnregistrer={(nom) => enregistrer(updateCollection({ id: collection.id, name: nom }))}
          />
        )}
      </CardContent>
    </Card>
  );
}

Bibliotheque.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  organization: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
  collection: PropTypes.object,
};
